import { useRef, useState, useEffect } from "react";
import { Box, Container, Typography, IconButton, Skeleton, Alert } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { postData } from "../../Webservice/Webservice";
import { useNavigate } from "react-router-dom";
import SectionHeading from "../../Components/SectionHeading/SectionHeading";

const rubikfont = {
  fontFamily: "Rubik, san-serif"
};

const CARD_W = { xs: 116, sm: 136, md: 156 };

const placeholderImage = "https://via.placeholder.com/250x250?text=No+Image";

function Category() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const navigate = useNavigate();

  const handlesubcategory = (categoryId, categoryTitle) => {
    const categorySlug = categoryTitle?.toLowerCase().replace(/\s+/g, '-') || 'category';
    navigate(`/${categorySlug}`, { state: { categoryId, categoryTitle } });
  };

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const tokenValue = localStorage.getItem("website_jwt_token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await postData('category/list', {}, token);

        if (response) {
          const activeCategories = response.categories.filter(
            category => category.status === 'active'
          );
          setCategories(activeCategories);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const autoScrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;

      let newPosition;
      if (direction === "left") {
        newPosition = scrollLeft - scrollAmount;
        if (newPosition < 0) newPosition = scrollWidth - clientWidth;
      } else {
        newPosition = scrollLeft + scrollAmount;
        if (scrollLeft + clientWidth >= scrollWidth - 10) newPosition = 0;
      }

      scrollContainerRef.current.scrollTo({
        left: newPosition,
        behavior: "smooth",
      });

      setTimeout(checkScrollButtons, 500);
    }
  };

  const startAutoScroll = () => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    autoScrollRef.current = setInterval(() => {
      scroll("right");
    }, 4000); // 4 seconds interval for readability
  };

  const stopAutoScroll = () => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
  };

  useEffect(() => {
    if (categories.length > 0) {
      startAutoScroll();
    }
    return () => stopAutoScroll();
  }, [categories]);

  // Loading state with skeleton
  if (loading) {
    return (
      <Box sx={{ py: { xs: 1, md: 2 }, padding: "0px 15px 0px 15px" }}>
        <SectionHeading kicker="Shop By Category" title="Our Collections" sx={{ px: { xs: 2, md: 5 } }} />


        <Box sx={{ position: "relative" }}>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 2, sm: 3, md: 4 },
              overflowX: "auto",
              "&::-webkit-scrollbar": { display: "none" },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
              py: 2,
              px: { xs: 5, sm: 6, md: 8 },
            }}
          >
            {[...Array(6)].map((_, index) => (
              <Box key={index} sx={{ width: CARD_W, flexShrink: 0 }}>
                <Skeleton variant="rectangular" sx={{ height: { xs: 150, sm: 170, md: 190 }, borderRadius: "18px" }} />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  // No categories found
  if (categories.length === 0) {
    return (
      <Container sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          variant="h6"
          align="center"
          sx={{ color: 'text.secondary' }}
        >
          No categories available at the moment.
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ py: { xs: 2.5, md: 3.5 }, bgcolor: "#fff", position: "relative" }}>
      <Container maxWidth="xl">
        {/* Standardized Layout Header */}
        <SectionHeading kicker="Shop By Category" title="Our Collections" />

        <Box sx={{ position: "relative" }}>
          {/* Left Arrow */}
          <IconButton
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            sx={{
              position: "absolute",
              left: -30,
              top: "40%",
              transform: "translateY(-50%)",
              zIndex: 10,
              bgcolor: "white",
              boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
              color: "#C8A054",
              "&:hover": { bgcolor: "#C8A054", color: "white" },
              "&.Mui-disabled": { opacity: 0, pointerEvents: "none" },
              width: 40,
              height: 40,
              display: { xs: "none", md: "flex" }
            }}
          >
            <ChevronLeft />
          </IconButton>

          {/*
            Scrollable category cards.
            The row has breathing room on every side (padding, pulled back with a negative margin), because a
            sideways scroller clips whatever sticks out of it: a card that lifts on hover, its gold border and its
            glow all stay fully visible, including the first and last card.
          */}
          <Box
            ref={scrollContainerRef}
            onScroll={checkScrollButtons}
            onMouseEnter={stopAutoScroll}
            onMouseLeave={startAutoScroll}
            sx={{
              display: "flex",
              gap: { xs: 1.5, sm: 2, md: 2.5 },
              overflowX: "auto",
              scrollBehavior: "smooth",
              "&::-webkit-scrollbar": { display: "none" },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
              px: 1.25,
              pt: 1.5,
              pb: 2.5,
              mx: -1.25,
              mt: -0.5,
            }}
          >
            {categories.map((category) => (
              <Box
                key={category._id}
                role="link"
                tabIndex={0}
                aria-label={`Shop ${category.title}`}
                onClick={() => handlesubcategory(category._id, category.title)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handlesubcategory(category._id, category.title); } }}
                sx={{
                  position: "relative",
                  width: CARD_W,
                  flexShrink: 0,
                  cursor: "pointer",
                  outline: "none",
                  borderRadius: "18px",
                  bgcolor: "#fff",
                  // the border belongs to the card itself, so it is never cut off and never shifts the layout
                  border: "1px solid #efe6d0",
                  boxShadow: "0 2px 8px rgba(60,40,10,0.05)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform .28s ease, box-shadow .28s ease, border-color .28s ease",
                  "&:hover, &:focus-visible": {
                    borderColor: "#C8A054",
                    transform: "translateY(-4px)",
                    boxShadow: "0 0 0 1px #C8A054, inset 0 -4px 0 0 #C8A054, 0 14px 28px rgba(200,160,84,0.24)",
                  },
                  "&:hover .cat-image, &:focus-visible .cat-image": { transform: "scale(1.08)" },
                  "&:hover .cat-title, &:focus-visible .cat-title": { color: "#9A7428" },
                  "&:hover .cat-shine, &:focus-visible .cat-shine": { opacity: 1 },
                  "&:active": { transform: "translateY(-1px)" },
                  "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover, &:focus-visible": { transform: "none" }, "& .cat-image, & .cat-shine": { transition: "none" } },
                }}
              >
                {/* photo */}
                <Box sx={{ position: "relative", height: { xs: 104, sm: 120, md: 138 }, overflow: "hidden", bgcolor: "#fbf5e6" }}>
                  <img
                    className="cat-image"
                    src={category.image || placeholderImage}
                    alt={category.title}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform .5s cubic-bezier(0.4, 0, 0.2, 1)" }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = placeholderImage;
                    }}
                  />
                  {/* a soft gold wash that fades in on hover */}
                  <Box className="cat-shine" sx={{ position: "absolute", inset: 0, opacity: 0, transition: "opacity .3s ease", background: "linear-gradient(180deg, rgba(200,160,84,0) 55%, rgba(200,160,84,0.28) 100%)", pointerEvents: "none" }} />
                </Box>

                {/* name (on hover the card's own bottom edge turns into a thicker gold line) */}
                <Box sx={{ position: "relative", px: 1, pt: 1.1, pb: 1.35, minHeight: { xs: 44, md: 48 }, display: "flex", alignItems: "center", justifyContent: "center", borderTop: "1px solid #f3ecd9", textAlign: "center" }}>
                  <Typography
                    className="cat-title"
                    title={category.title}
                    sx={{
                      fontSize: { xs: "11.5px", sm: "12.5px", md: "13.5px" },
                      fontWeight: 600,
                      color: "#333",
                      ...rubikfont,
                      lineHeight: 1.25,
                      transition: "color .2s ease",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {category.title}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Right Arrow */}
          <IconButton
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            sx={{
              position: "absolute",
              right: -30,
              top: "40%",
              transform: "translateY(-50%)",
              zIndex: 10,
              bgcolor: "white",
              boxShadow: "0 4px 15px rgba(0,0,0,0.06)",
              color: "#C8A054",
              "&:hover": { bgcolor: "#C8A054", color: "white" },
              "&.Mui-disabled": { opacity: 0, pointerEvents: "none" },
              width: 40,
              height: 40,
              display: { xs: "none", md: "flex" }
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Container>
    </Box>
  );
}

export default Category;
